import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import superAdminApiClient from '../../../api/superAdminApiClient';
import { formatDate } from '../../../utils/dateUtils';
import { formatPrice } from '../../../utils/priceUtils';
import { toast } from 'react-toastify';
import { buildVendorProfileUrl } from '../../../utils/vendorProfileUrl';

const VendorDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [vendor, setVendor] = useState(null);
    const [kycHistory, setKycHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Extend Validity Modal State
    const [showExtendModal, setShowExtendModal] = useState(false);
    const [plans, setPlans] = useState([]);
    const [extendForm, setExtendForm] = useState({
        plan_id: '',
        end_date: '',
        lead_limit: ''
    });
    const [extendLoading, setExtendLoading] = useState(false);

    const fetchVendorDetails = React.useCallback(async () => {
        try {
            const response = await superAdminApiClient.get(`/superadmin/vendors/${id}`);
            setVendor(response.data.vendor);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching vendor details:", err);
            setError("Failed to load vendor details.");
            setLoading(false);
        }
    }, [id]);

    const fetchKycHistory = React.useCallback(async () => {
        try {
            const response = await superAdminApiClient.get(`/superadmin/vendors/${id}/kyc-history`);
            setKycHistory(response.data.history);
        } catch (err) {
            console.error("Error fetching KYC history:", err);
        }
    }, [id]);

    useEffect(() => {
        fetchVendorDetails();
        fetchKycHistory();
    }, [fetchVendorDetails, fetchKycHistory]);

    const fetchPlans = async () => {
        try {
            const response = await superAdminApiClient.get('/superadmin/plans');
            if (response.data.success) {
                setPlans(response.data.plans.filter(p => p.status === 'active'));
            }
        } catch (err) {
            console.error("Error fetching plans:", err);
            toast.error('Failed to load plans.');
        }
    };

    const handleOpenExtendModal = () => {
        fetchPlans();
        setExtendForm({ plan_id: '', end_date: '', lead_limit: '' });
        setShowExtendModal(true);
    };

    const handleCloseExtendModal = () => {
        setShowExtendModal(false);
        setExtendForm({ plan_id: '', end_date: '', lead_limit: '' });
    };

    const handlePlanChange = (e) => {
        const selectedPlanId = e.target.value;
        const selectedPlan = plans.find(p => p.id === parseInt(selectedPlanId));

        let defaultEndDate = '';
        if (selectedPlan) {
            const end = new Date();
            end.setDate(end.getDate() + selectedPlan.duration_days);
            defaultEndDate = end.toISOString().split('T')[0];
        }

        setExtendForm({
            ...extendForm,
            plan_id: selectedPlanId,
            lead_limit: selectedPlan ? selectedPlan.leads_included : '',
            end_date: defaultEndDate
        });
    };

    const handleExtendFormChange = (e) => {
        setExtendForm({ ...extendForm, [e.target.name]: e.target.value });
    };

    const handleExtendSubmit = async (e) => {
        e.preventDefault();

        if (!extendForm.plan_id || !extendForm.end_date || extendForm.lead_limit === '') {
            toast.warn('Please fill in all fields.');
            return;
        }

        setExtendLoading(true);
        try {
            const response = await superAdminApiClient.post(`/superadmin/vendors/${id}/extend-validity`, {
                plan_id: parseInt(extendForm.plan_id),
                end_date: extendForm.end_date,
                lead_limit: parseInt(extendForm.lead_limit)
            });

            if (response.data.success) {
                toast.success(response.data.message || 'Subscription validity extended successfully!');
                handleCloseExtendModal();
                // Refresh vendor data to show new transaction
                fetchVendorDetails();
            } else {
                toast.error(response.data.message || 'Failed to extend validity.');
            }
        } catch (err) {
            console.error("Error extending validity:", err);
            const errorMessage = err.response?.data?.message || 'An error occurred while extending validity.';
            toast.error(errorMessage);
        } finally {
            setExtendLoading(false);
        }
    };

    // Get minimum date for date picker (tomorrow)
    const getMinDate = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow.toISOString().split('T')[0];
    };

    if (loading) return <div>Loading vendor details...</div>;
    if (error) return <div className="alert alert-danger">{error}</div>;
    if (!vendor) return <div>Vendor not found.</div>;

    const transactions = vendor.Transactions || [];
    let categories = [];

    try {
        let parsed = vendor.business_categories;
        while (typeof parsed === "string") {
            parsed = JSON.parse(parsed);
        }

        if (Array.isArray(parsed)) {
            categories = parsed;
        }

    } catch (e) {
        categories = [];
    }
    return (
        <React.Fragment>
            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 p-3 bg-white rounded shadow-sm border">
                        <div>
                            <h4 className="mb-1 text-dark fw-bold" style={{ fontSize: '1.2rem' }}>
                                Vendor Details: <span className="text-primary">{vendor.company_name || vendor.name}</span>
                            </h4>
                            <div className="text-muted small">
                                ID: #{vendor.id} {vendor.email ? `• ${vendor.email}` : ''} {vendor.phone ? `• ${vendor.phone}` : ''}
                            </div>
                        </div>
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            <a
                                href={buildVendorProfileUrl(vendor)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-info btn-sm text-white d-inline-flex align-items-center gap-1 px-3 py-1-5 fw-medium"
                                style={{ borderRadius: '6px' }}
                            >
                                <i className="bx bx-external-link fs-6"></i> Public Profile
                            </a>
                            <button
                                onClick={() => navigate(`/superadmin/vendors/detail/${id}/leads`)}
                                className="btn btn-primary btn-sm d-inline-flex align-items-center gap-1 px-3 py-1-5 fw-medium"
                                style={{ borderRadius: '6px' }}
                            >
                                <i className="bx bx-list-ul fs-6"></i> View Leads
                            </button>
                            {vendor.trashed_listings_count > 0 && (
                                <button
                                    onClick={() => navigate(`/superadmin/trash/vendors/${id}`)}
                                    className="btn btn-warning btn-sm d-inline-flex align-items-center gap-1 px-3 py-1-5 fw-medium text-dark"
                                    style={{ borderRadius: '6px' }}
                                    title="Manage Trashed Package Listings for this Vendor"
                                >
                                    <i className="bx bx-trash fs-6"></i> Trashed Listings ({vendor.trashed_listings_count})
                                </button>
                            )}
                            <button 
                                onClick={() => navigate(-1)} 
                                className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1 px-3 py-1-5 fw-medium"
                                style={{ borderRadius: '6px' }}
                            >
                                <i className="bx bx-arrow-back fs-6"></i> Back to List
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {vendor.trashed_listings_count > 0 && (
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="alert alert-warning border border-warning-subtle d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 m-0 p-3 rounded shadow-sm">
                            <div className="d-flex align-items-center gap-2">
                                <i className="bx bx-error-circle fs-4 text-warning"></i>
                                <span>
                                    This vendor has <strong>{vendor.trashed_listings_count} package listing(s)</strong> currently in Trash.
                                </span>
                            </div>
                            <button
                                onClick={() => navigate(`/superadmin/trash/vendors/${id}`)}
                                className="btn btn-warning btn-sm d-inline-flex align-items-center gap-1 fw-semibold text-dark px-3 py-1-5"
                                style={{ borderRadius: '6px' }}
                            >
                                <i className="bx bx-restore fs-6"></i> View & Restore Trashed Listings
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-header bg-light">
                            <h5 className="card-title mb-0">General Information</h5>
                        </div>
                        <div className="card-body">
                            <div className="row mb-3">
                                <div className="col-md-3"><strong>Name:</strong></div>
                                <div className="col-md-3">{vendor.name || 'N/A'}</div>
                                <div className="col-md-3"><strong>Company Name:</strong></div>
                                <div className="col-md-3">{vendor.company_name || 'N/A'}</div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-md-3"><strong>Email:</strong></div>
                                <div className="col-md-3">{vendor.email || 'N/A'}</div>
                                <div className="col-md-3"><strong>Phone:</strong></div>
                                <div className="col-md-3">{vendor.phone || 'N/A'}</div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-md-3"><strong>Address:</strong></div>
                                <div className="col-md-3">{vendor.company_address || 'N/A'}</div>
                                <div className="col-md-3"><strong>City / State:</strong></div>
                                <div className="col-md-3">{vendor.company_city ? vendor.company_city + ',' + vendor.company_state : "N/A"}</div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-md-3"><strong>Business Categories:</strong></div>
                                <div className="col-md-3">{categories.length ? (
                                    categories.map((cat, i) => (
                                        <span key={i} className="badge bg-primary me-1">
                                            {cat}
                                        </span>
                                    ))
                                ) : (
                                    'N/A'
                                )}</div>
                                <div className="col-md-3"><strong>Verification Status:</strong></div>
                                <div className="col-md-3">
                                    {vendor.is_aadhaar_verified ? <span className="badge bg-success">Verified</span> : <span className="badge bg-warning">Pending</span>}
                                </div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-md-3"><strong>KYC Document:</strong></div>
                                <div className="col-md-3">{vendor.kyc_type || 'N/A'} - {vendor.kyc_doc_number || 'N/A'}</div>
                                <div className="col-md-3"><strong>Registration Date:</strong></div>
                                <div className="col-md-3">{formatDate(vendor.createdAt, 'DD-MM-YYYY')}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header bg-light d-flex justify-content-between align-items-center">
                            <h5 className="card-title mb-0">Transactions & Subscriptions</h5>
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={handleOpenExtendModal}
                            >
                                <i className="ri-add-line me-1"></i> Extend Validity
                            </button>
                        </div>
                        <div className="card-body">
                            <div className="table-responsive">
                                <table className="table align-middle table-nowrap mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>SR No.</th>
                                            <th>Plan Name</th>
                                            <th>Amount</th>
                                            <th>Leads Assigned</th>
                                            <th>Date</th>
                                            <th>Status</th>
                                            <th>Payment Method</th>
                                            <th>Start Date</th>
                                            <th>End Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {transactions.length > 0 ? transactions.map((txn, index) => (
                                            <tr key={txn.id}>
                                                <td>{index + 1}</td>
                                                <td>{txn.plan_name || 'N/A'}</td>
                                                <td>₹{formatPrice(txn.amount)}</td>
                                                <td>
                                                    <span className="badge bg-light text-primary border border-primary-subtle fw-bold px-2 py-1">
                                                        {txn.leads_added ? `${txn.leads_added} Leads` : '0'}
                                                    </span>
                                                </td>
                                                <td>{formatDate(txn.transaction_date)}</td>
                                                <td>
                                                    <span className={`badge ${txn.payment_status === 'success' ? 'bg-success' : txn.payment_status === 'pending' ? 'bg-warning' : 'bg-danger'}`}>
                                                        {txn.payment_status.toUpperCase()}
                                                    </span>
                                                </td>
                                                <td>
                                                    {txn.payment_method === 'Super admin extend' ? (
                                                        <span className="badge bg-info">Admin Extended</span>
                                                    ) : (
                                                        txn.payment_method || 'N/A'
                                                    )}
                                                </td>
                                                <td>{txn.start_date ? formatDate(txn.start_date) : 'N/A'}</td>
                                                <td>{txn.end_date ? formatDate(txn.end_date) : 'N/A'}</td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="9" className="text-center">No transactions found for this vendor.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row mt-3">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header bg-light d-flex justify-content-between align-items-center">
                            <h5 className="card-title mb-0">KYC Update History</h5>
                        </div>
                        <div className="card-body">
                            <div className="table-responsive">
                                <table className="table align-middle table-nowrap mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Date</th>
                                            <th>Action</th>
                                            <th>KYC Type</th>
                                            <th>GST/Aadhaar Number</th>
                                            <th>Company/Vendor Name</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {kycHistory.length > 0 ? kycHistory.map((hist) => (
                                            <tr key={hist.id}>
                                                <td>{formatDate(hist.createdAt)}</td>
                                                <td><span className="badge bg-info">{hist.action_type || 'UPDATED'}</span></td>
                                                {hist.kyc_type === "GST Number" ? (
                                                    <>
                                                        <td>GST Verification</td>
                                                        <td>{hist.gst_number}</td>
                                                        <td>{hist.company_name || "N/A"}</td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td>Aadhar Verification</td>
                                                        <td>{hist.personal_aadhaar}</td>
                                                        <td>{hist.personal_name || "N/A"}</td>
                                                    </>
                                                )}
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="5" className="text-center">No KYC history found for this vendor.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Extend Validity Modal */}
            {showExtendModal && (
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
                    <div className="modal fade show d-block extend-plan-validity" tabIndex="-1" style={{ zIndex: 1050 }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header bg-primary text-white pb-3">
                                    <h5 className="modal-title">
                                        <i className="ri-calendar-check-line me-2"></i>
                                        Extend Subscription Validity
                                    </h5>
                                    <button type="button" className="btn-close btn-close-white" onClick={handleCloseExtendModal} disabled={extendLoading}></button>
                                </div>
                                <form onSubmit={handleExtendSubmit}>
                                    <div className="modal-body">
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold">Select Plan <span className="text-danger">*</span></label>
                                            <select
                                                className="form-select"
                                                name="plan_id"
                                                value={extendForm.plan_id}
                                                onChange={handlePlanChange}
                                                required
                                                disabled={extendLoading}
                                            >
                                                <option value="">-- Choose a Plan --</option>
                                                {plans.map(plan => (
                                                    <option key={plan.id} value={plan.id}>
                                                        {plan.name} — ₹{formatPrice(plan.price)} ({plan.duration_days} days, {plan.leads_included} leads)
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label fw-semibold">Plan End Date <span className="text-danger">*</span></label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                name="end_date"
                                                value={extendForm.end_date}
                                                onChange={handleExtendFormChange}
                                                min={getMinDate()}
                                                required
                                                disabled={extendLoading}
                                            />
                                            <small className="text-muted">Subscription will start from today and end on selected date.</small>
                                        </div>

                                        <div className="mb-3">
                                            <label className="form-label fw-semibold">Lead Limit <span className="text-danger">*</span></label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                name="lead_limit"
                                                value={extendForm.lead_limit}
                                                onChange={handleExtendFormChange}
                                                min="0"
                                                required
                                                disabled={extendLoading}
                                            />
                                            <small className="text-muted">Number of leads to add to vendor's available leads (default from selected plan).</small>
                                        </div>

                                        <div className="alert alert-info mb-0 py-2" style={{ fontSize: '13px' }}>
                                            <i className="ri-information-line me-1"></i>
                                            No payment will be charged. Transaction will be recorded as <strong>Super admin extend</strong> with ₹0.00 amount. An email notification will be sent to the vendor.
                                        </div>
                                    </div>
                                    <div className="modal-footer">
                                        <button type="button" className="btn btn-light" onClick={handleCloseExtendModal} disabled={extendLoading}>
                                            Cancel
                                        </button>
                                        <button type="submit" className="btn btn-primary" disabled={extendLoading}>
                                            {extendLoading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                    Extending...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="ri-check-line me-1"></i> Extend Validity
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </React.Fragment>
    );
};

export default VendorDetail;
